// Build a self-contained letterhead HTML document for printing.
// Consumed by an off-screen iframe; the browser's own engine
// renders Arabic correctly (proper bidi + shaping) and the resulting
// PDF (via "Save as PDF") preserves selectable text.
//
// Page layout strategy:
//   * @page margins reserve a generous bottom strip on EVERY page.
//   * Per-page signature lines are rendered into @bottom-left /
//     @bottom-center / @bottom-right (CSS Paged Media). This is the
//     ONLY cross-browser-safe way to put content at the foot of every
//     printed page — `position: fixed` only paints on the first page
//     in Chromium.
//   * Body content carries a real typographic hierarchy:
//       H1   → big centered gold-bordered banner (the contract title)
//       H2   → section heading (الديباجة، التمهيد، التوقيعات…)
//       BOLD → clause head line («البند الأول: موضوع العقد»)
//       PARA → justified body paragraph

// Transparent PNG of the firm logo. We compute the absolute URL at
// call-time so the print iframe (which writes its own document and
// therefore has no <base>) still resolves the asset correctly.
const LOGO_PATH = "/branding/logo-transparent.png";
const LOGO_URL =
  typeof window !== "undefined" && window.location
    ? `${window.location.origin}${LOGO_PATH}`
    : LOGO_PATH;

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// Inline Markdown — handle **bold** and *italic* spans inside an
// already escaped paragraph. We must run AFTER escapeHtml so the
// asterisks don't collide with text that legitimately contains them.
function inlineMd(s) {
  return s
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/(?<!\*)\*(?!\*)([^*]+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');
}

// Detect if a line is mostly a numbered/lettered clause head like
// «1.» «1-» «أولاً» «أولا:» «ثانياً» «البند الأول» «المادة الأولى» etc.
// We treat these as clause heads only when the line is SHORT (<= 100
// chars) so we don't mis-classify a body sentence that happens to
// start with a number.
function looksLikeClauseHead(line) {
  if (line.length > 100) return false;
  return (
    /^\s*\d+\s*[.\-)]\s*/.test(line) ||
    /^\s*(?:أولاً|ثانياً|ثالثاً|رابعاً|خامساً|سادساً|سابعاً|ثامناً|تاسعاً|عاشراً|أولا|ثانيا|ثالثا|رابعا|خامسا|سادسا|سابعا|ثامنا|تاسعا|عاشرا)\s*[:\-،]/.test(line) ||
    /^\s*(?:البند|المادة|الفقرة|القسم|الفصل)\s+(?:الأول|الثاني|الثالث|الرابع|الخامس|السادس|السابع|الثامن|التاسع|العاشر|الحادي عشر|الثاني عشر|الأولى|الثانية|الثالثة|الرابعة|الخامسة|السادسة|السابعة|الثامنة|التاسعة|العاشرة)/.test(line) ||
    /^\s*(?:Article|Clause|Section|Part)\s+(?:[IVX]+|\d+)\b/i.test(line)
  );
}

// Strip stray party-signature lines from the BODY of the contract so
// they only appear in the final `## التوقيعات` / `## Signatures`
// section. The model occasionally repeats «توقيع الطرف الأول: ____»
// or an English equivalent inside individual clauses — Dr. Aldakheel's
// drafting guide treats this as a duplicate and the firm wants the
// PDF to mirror that discipline.
//
// We detect signature-leak lines via three signals:
//   1. The line literally starts with «توقيع» / «Signature» followed
//      by a colon or underscore run.
//   2. A leftover «الطرف الأول/الثاني: ____» without the «التوقيعات»
//      heading on the previous lines.
//   3. A line that is *only* an underscore rule (`______`) and that
//      sits OUTSIDE the trailing signatures section.
// The trailing `## التوقيعات` block itself is preserved untouched.
function stripDuplicateSignatures(text) {
  const lines = text.split("\n");
  // Locate the signatures heading (last occurrence wins).
  let sigStart = -1;
  for (let i = lines.length - 1; i >= 0; i -= 1) {
    if (/^\s*##\s+(?:التوقيعات|Signatures|Signature)\s*$/i.test(lines[i])) {
      sigStart = i;
      break;
    }
  }
  const headRe =
    /^\s*\*{0,2}\s*(?:توقيع|Signature)\s*\*{0,2}\s*[:：\-_].*/i;
  const partyLabelRe =
    /^\s*\*{0,2}\s*(?:الطرف\s+(?:الأول|الثاني)|Party\s+(?:One|Two|1|2))\s*\*{0,2}\s*[:：].*[_]{3,}.*$/i;
  const ruleRe = /^\s*[_\-]{8,}\s*$/;
  return lines
    .map((ln, idx) => {
      const insideSig = sigStart >= 0 && idx >= sigStart;
      if (insideSig) return ln;
      if (headRe.test(ln)) return null;
      if (partyLabelRe.test(ln)) return null;
      if (ruleRe.test(ln)) return null;
      return ln;
    })
    .filter((ln) => ln !== null)
    .join("\n");
}

function renderBody(content) {
  // Pre-process to remove duplicate party-signature lines that
  // sometimes leak into the body (e.g. a clause that ends with
  // «توقيع الطرف الأول: ____» mid-document). The dedicated
  // `## التوقيعات` / `## Signatures` section at the foot of the
  // contract is the ONLY allowed location for signature blocks.
  // We also collapse repeated `---` style separators that the model
  // sometimes injects between clauses.
  const _stripped = stripDuplicateSignatures(content || "");
  const rawLines = _stripped.split("\n");
  let titleRendered = false;
  let lastWasHeading = false;
  let blankRun = 0;
  const out = [];

  rawLines.forEach((rawLine) => {
    const line = rawLine.replace(/\s+$/, "");
    const trimmed = line.trim();

    if (trimmed === "") {
      // Collapse blanks aggressively: a single blank line between
      // clauses is enough. Tightening this is what makes the contract
      // actually fit on two pages instead of three.
      blankRun += 1;
      if (blankRun > 1) return;
      if (out.length && !out[out.length - 1].endsWith("</p>")) return;
      out.push(`<p class="spacer">&nbsp;</p>`);
      lastWasHeading = false;
      return;
    }
    blankRun = 0;

    // ---- H1 — # Title ------------------------------------------
    const h1 = /^\s*#\s+(.+?)\s*$/.exec(line);
    if (h1 && !titleRendered) {
      titleRendered = true;
      out.push(`<h1 class="contract-title">${inlineMd(escapeHtml(h1[1]))}</h1>`);
      lastWasHeading = true;
      return;
    }
    // ---- H2 — ## Section ---------------------------------------
    const h2 = /^\s*##\s+(.+?)\s*$/.exec(line);
    if (h2) {
      out.push(`<h2 class="section">${inlineMd(escapeHtml(h2[1]))}</h2>`);
      lastWasHeading = true;
      return;
    }
    // ---- H3 — ### Sub-section ----------------------------------
    const h3 = /^\s*###\s+(.+?)\s*$/.exec(line);
    if (h3) {
      out.push(`<h3 class="subsection">${inlineMd(escapeHtml(h3[1]))}</h3>`);
      lastWasHeading = true;
      return;
    }
    // ---- "All-bold" line treated as a clause head --------------
    const allBold = /^\s*\*\*([^*]+?)\*\*\s*:?\s*$/.exec(line);
    if (allBold) {
      out.push(`<p class="clause-head">${inlineMd(escapeHtml(allBold[1]))}</p>`);
      lastWasHeading = true;
      return;
    }
    // ---- Numbered / ordinal clause head (no markdown) ----------
    if (looksLikeClauseHead(trimmed) && !lastWasHeading) {
      out.push(`<p class="clause-head">${inlineMd(escapeHtml(trimmed))}</p>`);
      lastWasHeading = true;
      return;
    }
    // ---- Signature line ----------------------------------------
    if (/^\s*[-_]{3,}\s*$/.test(line)) {
      out.push(`<p class="sig-line">&nbsp;</p>`);
      lastWasHeading = false;
      return;
    }
    // ---- First non-empty line if no title yet ------------------
    if (!titleRendered) {
      titleRendered = true;
      out.push(`<h1 class="contract-title">${inlineMd(escapeHtml(trimmed))}</h1>`);
      lastWasHeading = true;
      return;
    }
    // ---- Default: body paragraph -------------------------------
    out.push(`<p class="body-p">${inlineMd(escapeHtml(line))}</p>`);
    lastWasHeading = false;
  });

  return out.join("\n");
}

export function buildLetterheadHtml({ content, language = "en" }) {
  const isAr = language === "ar";
  const dir = isAr ? "rtl" : "ltr";
  const lang = isAr ? "ar" : "en";

  const arFont = '"Noto Naskh Arabic", "Amiri", "Cairo", serif';
  const enFont = "'Outfit', sans-serif";
  const headingFont = isAr ? arFont : "'Fraunces', serif";
  const bodyFont = isAr ? arFont : enFont;
  const footerFont = isAr ? arFont : enFont;

  // Footer note printed on every page in the @page bottom box.
  // The firm name was removed from the masthead per request — we keep
  // the logo only — and now the bottom strip carries the provenance
  // notice in its place.
  const generatedNote = isAr
    ? "تم إنشاء هذا العقد عبر موقع aldakheellg.com"
    : "Generated via aldakheellg.com";

  // Signature footer labels — appear on every page via @page boxes.
  const partyOne = isAr ? "الطرف الأول" : "Party One";
  const partyTwo = isAr ? "الطرف الثاني" : "Party Two";
  const sigLine = "____________________";
  const pageWord = isAr ? "صفحة" : "Page";
  const ofWord = isAr ? "من" : "of";

  const body = renderBody(content);

  return `<!DOCTYPE html>
<html lang="${lang}" dir="${dir}">
<head>
  <meta charset="UTF-8" />
  <title>Aldakheel Legal Group</title>
  <link rel="preconnect" href="https://fonts.googleapis.com" />
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
  <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=Cairo:wght@300;400;500;600;700&family=Noto+Naskh+Arabic:wght@400;500;600;700&family=Amiri:wght@400;700&family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700&display=swap" rel="stylesheet" />
  <style>
    /* ---------- Page setup ------------------------------------- */
    @page {
      size: A4;
      margin: 16mm 16mm 32mm 16mm;
      background: #FAF6EC;
      @bottom-left {
        content: "${partyOne}: ${sigLine}";
        font-family: ${footerFont};
        font-size: 9pt;
        color: #1c1815;
        padding-top: 4mm;
        vertical-align: top;
      }
      @bottom-center {
        content: "${generatedNote}    ·    ${pageWord} " counter(page) " / " counter(pages);
        font-family: ${footerFont};
        font-size: 7.5pt;
        color: #8a7448;
        padding-top: 10mm;
        vertical-align: top;
      }
      @bottom-right {
        content: "${partyTwo}: ${sigLine}";
        font-family: ${footerFont};
        font-size: 9pt;
        color: #1c1815;
        padding-top: 4mm;
        vertical-align: top;
      }
    }

    html, body {
      margin: 0;
      padding: 0;
      background: #FAF6EC;
      color: #1c1815;
      font-family: ${bodyFont};
      font-size: 9.5pt;
      line-height: 1.45;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    /* ---------- Letterhead masthead — logo only, no firm name ---- */
    .header {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      padding-bottom: 6pt;
      border-bottom: 0.75pt solid #c9a24b;
      margin-bottom: 12pt;
    }
    .logo {
      height: 56pt;
      width: auto;
      object-fit: contain;
    }
    .watermark {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 55%;
      max-width: 340pt;
      opacity: 0.04;
      pointer-events: none;
      z-index: 0;
    }
    .header, .body { position: relative; z-index: 1; }

    /* ---------- Typographic hierarchy -------------------------- */

    /* H1 — contract title (gold-rule banner). One per document. */
    .contract-title {
      font-family: ${headingFont};
      font-size: ${isAr ? "16pt" : "15pt"};
      font-weight: 700;
      text-align: center;
      color: #1c1815;
      letter-spacing: ${isAr ? "0" : "0.02em"};
      margin: 2pt 0 12pt;
      padding: 6pt 0;
      border-top: 0.6pt solid #c9a24b;
      border-bottom: 0.6pt solid #c9a24b;
      page-break-after: avoid;
      break-after: avoid;
      line-height: 1.2;
    }

    /* H2 — section heading (التمهيد، التوقيعات…)
       Sized just one notch above clause heads, gold accent. */
    .body h2.section {
      font-family: ${headingFont};
      font-size: ${isAr ? "11pt" : "10.5pt"};
      font-weight: 700;
      color: #1c1815;
      margin: 8pt 0 4pt;
      padding-bottom: 2pt;
      border-bottom: 0.4pt solid #c9a24b;
      letter-spacing: ${isAr ? "0" : "0.06em"};
      text-transform: ${isAr ? "none" : "uppercase"};
      page-break-after: avoid;
      break-after: avoid;
    }

    /* H3 — sub-section heading. */
    .body h3.subsection {
      font-family: ${headingFont};
      font-size: ${isAr ? "10pt" : "10pt"};
      font-weight: 600;
      color: #2d2620;
      margin: 6pt 0 3pt;
      page-break-after: avoid;
      break-after: avoid;
    }

    /* Clause head — bold, slightly larger than body, gold accent. */
    .body p.clause-head {
      font-family: ${bodyFont};
      font-size: ${isAr ? "10pt" : "9.5pt"};
      font-weight: 700;
      color: #1c1815;
      margin: 6pt 0 1.5pt;
      padding-${isAr ? "right" : "left"}: 5pt;
      border-${isAr ? "right" : "left"}: 1.5pt solid #c9a24b;
      text-align: ${isAr ? "right" : "left"};
      page-break-after: avoid;
      break-after: avoid;
      line-height: 1.3;
    }

    /* Body paragraph — justified, compact leading so a 2-page draft
       reliably fits within the @page margins. */
    .body p.body-p {
      margin: 0 0 2pt 0;
      font-size: 9.5pt;
      line-height: 1.5;
      text-align: justify;
      text-justify: inter-word;
      hyphens: auto;
      color: #1c1815;
      white-space: pre-wrap;
      word-wrap: break-word;
      orphans: 3;
      widows: 3;
    }
    /* Strong inside body — slightly darker than body so it doesn't
       compete with clause heads. */
    .body p.body-p strong { color: #1c1815; font-weight: 700; }
    .body p.body-p em     { font-style: italic; color: #2d2620; }

    .body p.spacer {
      margin: 1pt 0;
      line-height: 0.5;
      font-size: 0;
    }
    .body p.sig-line {
      border-top: 0.6pt solid #1c1815;
      margin: 16pt 0 4pt;
      width: 60%;
    }

    /* Signature-block paragraph (kept for backwards compatibility). */
    .body p.sig {
      text-align: ${isAr ? "right" : "left"};
      font-weight: 600;
      margin-top: 14pt;
      page-break-after: avoid;
      break-after: avoid;
    }

    /* ---------- Firm strip (bottom of last page) --------------- */
  </style>
</head>
<body dir="${dir}" lang="${lang}">
  <img class="watermark" src="${LOGO_URL}" alt="" />
  <div class="header">
    <img class="logo" src="${LOGO_URL}" alt="" crossorigin="anonymous" />
  </div>
  <div class="body" dir="${dir}">${body}</div>
</body>
</html>`;
}

export async function printLetterhead({ content, language }) {
  const html = buildLetterheadHtml({ content, language });
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.setAttribute("aria-hidden", "true");
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();

  // Wait for fonts and the logo image to load before printing.
  await new Promise((resolve) => {
    const fonts = (doc.fonts && doc.fonts.ready) || Promise.resolve();
    const img = doc.querySelector("img.logo");
    const imgReady =
      img && !img.complete
        ? new Promise((r) => {
            img.onload = r;
            img.onerror = r;
          })
        : Promise.resolve();
    Promise.all([fonts, imgReady])
      .then(() => setTimeout(resolve, 250))
      .catch(() => setTimeout(resolve, 600));
  });

  iframe.contentWindow.focus();
  iframe.contentWindow.print();

  // Remove the iframe after the print dialog is dismissed.
  setTimeout(() => {
    if (iframe && iframe.parentNode) iframe.parentNode.removeChild(iframe);
  }, 2000);
}
